import axios from 'axios'
import type { CrowdTask, CrowdSubmission, AdminTaskDetail, Category, CrowdProduct, CrowdMerchant, CrowdMerchantDetail, AdminWorker, AdminWorkerDetail, LeaderboardEntry } from '../types'

const adminClient = axios.create({ baseURL: '/api' })

export function setAdminApiKey(key: string) {
  if (key) {
    adminClient.defaults.headers.common['X-Admin-Key'] = key
  } else {
    delete adminClient.defaults.headers.common['X-Admin-Key']
  }
}

export interface AdminTaskItemCreate {
  product_name: string
  product_description?: string
  category_id?: string
  sort_order: number
  translations?: Record<string, { product_name?: string; product_description?: string }>
}

export interface AdminTaskCreate {
  title: string
  description?: string
  task_type: 'spot' | 'batch'
  category_id?: string
  target_location_name?: string
  target_latitude?: number
  target_longitude?: number
  target_radius_km?: number
  expires_at: string // ISO datetime string
  max_claims: number
  reward_per_item_coins?: number
  header_image_url?: string
  items: AdminTaskItemCreate[]
  translations?: Record<string, { title?: string; description?: string }>
}

export interface AdminTaskUpdate {
  title?: string
  description?: string
  category_id?: string
  target_location_name?: string
  expires_at?: string
  max_claims?: number
  reward_per_item_coins?: number
  header_image_url?: string
  translations?: Record<string, { title?: string; description?: string }>
}

export interface AdminStats {
  tasks_by_status: Record<string, number>
  submissions_by_status: Record<string, number>
  active_workers: number
  completed_assignments: number
  total_products?: number
  products_by_status?: Record<string, number>
  total_submissions?: number
  submissions_today?: number
}

export interface WorkersParams {
  skip?: number
  limit?: number
  search?: string
}

export async function listAllTasks(
  skip = 0,
  limit = 50,
): Promise<{ items: CrowdTask[]; total: number }> {
  const res = await adminClient.get('/v1/crowd/admin/tasks', { params: { skip, limit } })
  return res.data
}

export async function createTask(data: AdminTaskCreate): Promise<CrowdTask> {
  const res = await adminClient.post('/v1/crowd/admin/tasks', data)
  return res.data
}

export async function publishTask(taskId: string): Promise<CrowdTask> {
  const res = await adminClient.post(`/v1/crowd/admin/tasks/${taskId}/publish`)
  return res.data
}

export async function listPendingSubmissions(
  skip = 0,
  limit = 50,
): Promise<CrowdSubmission[]> {
  const res = await adminClient.get('/v1/crowd/admin/submissions', { params: { skip, limit } })
  return res.data
}

export async function verifySubmission(
  submissionId: string,
  status: 'verified' | 'rejected',
  comment?: string,
  auto_publish = false,
): Promise<CrowdSubmission> {
  const res = await adminClient.post(`/v1/crowd/admin/submissions/${submissionId}/verify`, {
    status,
    comment,
    auto_publish,
  })
  return res.data
}

export async function updateTask(taskId: string, data: AdminTaskUpdate): Promise<CrowdTask> {
  const res = await adminClient.put(`/v1/crowd/admin/tasks/${taskId}`, data)
  return res.data
}

export async function getTaskDetail(taskId: string): Promise<AdminTaskDetail> {
  const res = await adminClient.get(`/v1/crowd/admin/tasks/${taskId}`)
  return res.data
}

export async function listCategories(): Promise<Category[]> {
  const res = await adminClient.get('/v1/categories')
  return res.data
}

export interface CrowdProductsParams {
  skip?: number
  limit?: number
  search?: string
  status?: string
  category_id?: string
}

export async function listCrowdProducts(
  params: CrowdProductsParams = {},
): Promise<{ items: CrowdProduct[]; total: number }> {
  const res = await adminClient.get('/v1/crowd/admin/products', { params: { limit: 50, ...params } })
  return res.data
}

export async function getStats(): Promise<AdminStats> {
  const res = await adminClient.get('/v1/crowd/admin/stats')
  return res.data
}

export async function publishProduct(productId: string): Promise<void> {
  await adminClient.post(`/v1/crowd/admin/products/${productId}/publish`)
}

export async function archiveProduct(productId: string): Promise<void> {
  await adminClient.post(`/v1/crowd/admin/products/${productId}/archive`)
}

export async function bulkPublishProducts(productIds: string[]): Promise<void> {
  await adminClient.post('/v1/crowd/admin/products/bulk-publish', { product_ids: productIds })
}

export interface CrowdMerchantsParams {
  skip?: number
  limit?: number
  search?: string
  verified?: boolean
}

export async function listCrowdMerchants(
  params: CrowdMerchantsParams = {},
): Promise<{ items: CrowdMerchant[]; total: number }> {
  const res = await adminClient.get('/v1/crowd/admin/merchants', { params: { limit: 50, ...params } })
  return res.data
}

export async function getCrowdMerchantDetail(merchantId: string): Promise<CrowdMerchantDetail> {
  const res = await adminClient.get(`/v1/crowd/admin/merchants/${merchantId}`)
  return res.data
}

export async function verifyCrowdMerchant(merchantId: string): Promise<void> {
  await adminClient.post(`/v1/crowd/admin/merchants/${merchantId}/verify`)
}

export interface MerchantAnalyticsTimeSeriesPoint {
  date: string
  count: number
}

export interface MerchantAnalyticsTopProduct {
  product_id: string
  name: string
  views: number
  likes: number
  thumbnail_url: string | null
  conversion_rate: number | null
  top_source: string | null
}

export interface MerchantAnalyticsInboundSearch {
  query: string
  view_count: number
}

export interface MerchantAnalyticsFunnelViaShop {
  visited_shop: number
  opened_product: number
  added_to_favorites: number
  messaged_seller: number
}

export interface MerchantAnalyticsFunnelViaDirect {
  opened_product: number
  added_to_favorites: number
  messaged_seller: number
}

export interface MerchantAnalytics {
  period_days: number
  total_views: number
  total_likes_period: number
  conversion_rate: number
  views_over_time: MerchantAnalyticsTimeSeriesPoint[]
  likes_over_time: MerchantAnalyticsTimeSeriesPoint[]
  top_viewed_products: MerchantAnalyticsTopProduct[]
  source_breakdown: Record<string, number> | null
  platform_breakdown: { ios: number; android: number; web: number } | null
  merchant_page_views: number | null
  merchant_page_views_over_time: MerchantAnalyticsTimeSeriesPoint[] | null
  merchant_page_unique_visitors: number | null
  merchant_page_unique_visitors_over_time: MerchantAnalyticsTimeSeriesPoint[] | null
  repeat_visitor_count: number | null
  repeat_visitor_rate: number | null
  top_inbound_searches: MerchantAnalyticsInboundSearch[] | null
  funnel_via_shop: MerchantAnalyticsFunnelViaShop | null
  funnel_via_direct: MerchantAnalyticsFunnelViaDirect | null
}

export async function getCrowdMerchantAnalytics(
  merchantId: string,
  days = 30,
): Promise<MerchantAnalytics> {
  const res = await adminClient.get(`/v1/crowd/admin/merchants/${merchantId}/analytics`, {
    params: { days },
  })
  return res.data
}

export async function assignMerchantOwner(
  merchantId: string,
  data: { telegram_id?: number; telegram_username?: string },
): Promise<void> {
  await adminClient.post(`/v1/crowd/admin/merchants/${merchantId}/assign-owner`, data)
}

export async function listWorkers(
  params: WorkersParams = {},
): Promise<{ items: AdminWorker[]; total: number }> {
  const res = await adminClient.get('/v1/crowd/admin/workers', { params: { limit: 50, ...params } })
  return res.data
}

export async function getWorkerDetail(workerId: string): Promise<AdminWorkerDetail> {
  const res = await adminClient.get(`/v1/crowd/admin/workers/${workerId}`)
  return res.data
}

export async function getLeaderboard(): Promise<LeaderboardEntry[]> {
  const res = await adminClient.get('/v1/crowd/admin/workers/leaderboard')
  return res.data
}

export async function uploadTaskGalleryImage(taskId: string, file: File): Promise<CrowdTask> {
  const form = new FormData()
  form.append('file', file)
  const res = await adminClient.post(`/v1/crowd/admin/tasks/${taskId}/images`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export async function deleteTaskGalleryImage(taskId: string, imageId: string): Promise<CrowdTask> {
  const res = await adminClient.delete(`/v1/crowd/admin/tasks/${taskId}/images/${imageId}`)
  return res.data
}

export async function uploadTaskItemGalleryImage(
  taskId: string,
  itemId: string,
  file: File,
): Promise<CrowdTask> {
  const form = new FormData()
  form.append('file', file)
  const res = await adminClient.post(
    `/v1/crowd/admin/tasks/${taskId}/items/${itemId}/images`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return res.data
}

export async function deleteTaskItemGalleryImage(
  taskId: string,
  itemId: string,
  imageId: string,
): Promise<CrowdTask> {
  const res = await adminClient.delete(
    `/v1/crowd/admin/tasks/${taskId}/items/${itemId}/images/${imageId}`,
  )
  return res.data
}

export async function uploadTaskItemImage(
  taskId: string,
  itemId: string,
  file: File,
): Promise<CrowdTask> {
  const form = new FormData()
  form.append('file', file)
  const res = await adminClient.post(
    `/v1/crowd/admin/tasks/${taskId}/items/${itemId}/upload-image`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return res.data
}

export async function uploadTaskImage(file: File): Promise<{ url: string }> {
  const form = new FormData()
  form.append('file', file)
  const res = await adminClient.post('/v1/crowd/admin/upload-image', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data
}

export async function addTaskItem(
  taskId: string,
  data: AdminTaskItemCreate,
): Promise<CrowdTask> {
  const res = await adminClient.post(`/v1/crowd/admin/tasks/${taskId}/items`, data)
  return res.data
}

export async function updateTaskItem(
  taskId: string,
  itemId: string,
  data: import('../types').CrowdTaskItemUpdate,
): Promise<CrowdTask> {
  const res = await adminClient.put(`/v1/crowd/admin/tasks/${taskId}/items/${itemId}`, data)
  return res.data
}

export async function deleteTaskItem(taskId: string, itemId: string): Promise<CrowdTask> {
  const res = await adminClient.delete(`/v1/crowd/admin/tasks/${taskId}/items/${itemId}`)
  return res.data
}

// ── Admin Analytics ────────────────────────────────────────────────────────

export interface DailySeries {
  date: string
  product_views: number
  merchant_views: number
}

export interface AnalyticsOverview {
  period_days: number
  total_product_views: number
  total_merchant_views: number
  active_users: number
  daily_series: DailySeries[]
  platform_breakdown: { ios: number; android: number; web: number }
  zero_result_search_rate: number
}

export interface MerchantLeaderboardEntry {
  rank: number
  merchant_id: string
  merchant_name: string
  product_views: number
  page_views: number
  likes: number
  conversion_rate: number
}

export interface SearchGaps {
  period_days: number
  total_searches: number
  zero_result_searches: number
  zero_result_rate: number
  top_zero_result_queries: { query: string; count: number }[]
}

export interface SourceTrendEntry {
  date: string
  direct: number
  search: number
  category: number
  shared: number
}

export interface SourceBreakdown {
  period_days: number
  totals: {
    direct: number
    search: number
    category: number
    collection: number
    merchant_page: number
    shared: number
    section_link: number
  }
  daily_trend: SourceTrendEntry[]
}

export interface WeekRetention {
  week: string
  total_active: number
  new_users: number
  returning_users: number
}

export interface Retention {
  period_days: number
  total_unique_users: number
  repeat_visitor_count: number
  repeat_visitor_rate: number
  new_vs_returning_weekly: WeekRetention[]
  language_breakdown: { language: string; users: number }[]
}

export async function getAdminAnalyticsOverview(days = 30): Promise<AnalyticsOverview> {
  const res = await adminClient.get('/v1/admin/analytics/overview', { params: { days } })
  return res.data
}

export async function getAdminAnalyticsMerchants(
  days = 30,
  limit = 50,
): Promise<MerchantLeaderboardEntry[]> {
  const res = await adminClient.get('/v1/admin/analytics/merchants', { params: { days, limit } })
  return res.data
}

export async function getAdminAnalyticsSearchGaps(days = 30, limit = 20): Promise<SearchGaps> {
  const res = await adminClient.get('/v1/admin/analytics/search-gaps', { params: { days, limit } })
  return res.data
}

export async function getAdminAnalyticsSources(days = 30): Promise<SourceBreakdown> {
  const res = await adminClient.get('/v1/admin/analytics/sources', { params: { days } })
  return res.data
}

export async function getAdminAnalyticsRetention(days = 30): Promise<Retention> {
  const res = await adminClient.get('/v1/admin/analytics/retention', { params: { days } })
  return res.data
}

export interface ChatContext {
  period_days: number
  chat_type_breakdown: {
    chat_type: string
    product_views: number
    merchant_views: number
    unique_instances: number
  }[]
  top_chat_instances: {
    chat_instance: string
    chat_type: string
    product_views: number
    merchant_views: number
    total_views: number
    last_seen_at: string | null
  }[]
}

export async function getAdminAnalyticsChatContext(
  days = 30,
  topInstances = 50,
): Promise<ChatContext> {
  const res = await adminClient.get('/v1/admin/analytics/chat-context', {
    params: { days, top_instances: topInstances },
  })
  return res.data
}

// ---------------------------------------------------------------------------
// Invite codes
// ---------------------------------------------------------------------------

export interface ShopInviteCode {
  id: string
  code: string
  note: string | null
  status: 'ACTIVE' | 'USED' | 'REVOKED'
  used_by_user_id: string | null
  used_at: string | null
  created_at: string
}

export async function listInviteCodes(): Promise<ShopInviteCode[]> {
  const res = await adminClient.get('/v1/crowd/admin/invite-codes')
  return res.data
}

export async function createInviteCode(note?: string): Promise<ShopInviteCode> {
  const res = await adminClient.post('/v1/crowd/admin/invite-codes', { note: note || null })
  return res.data
}

export async function revokeInviteCode(id: string): Promise<void> {
  await adminClient.delete(`/v1/crowd/admin/invite-codes/${id}`)
}

// ── Backoffice: Merchant members ─────────────────────────────────────────────

export type MerchantMemberRole = 'OWNER' | 'ADMIN' | 'VIEWER'

export interface MerchantMember {
  id: string
  merchant_id: string
  user_id: string
  role: MerchantMemberRole
  telegram_id: number | null
  telegram_username: string | null
  first_name: string | null
  last_name: string | null
  photo_url: string | null
  created_at: string | null
}

export interface MerchantMembersParams {
  skip?: number
  limit?: number
  search?: string
}

export interface MerchantMembersResponse {
  items: MerchantMember[]
  total: number
}

export async function listMerchantMembers(
  merchantId: string,
  params: MerchantMembersParams = {},
): Promise<MerchantMembersResponse> {
  const res = await adminClient.get(
    `/v1/crowd/admin/merchants/${merchantId}/members`,
    { params: { limit: 50, ...params } },
  )
  return res.data
}

export async function addMerchantMember(
  merchantId: string,
  data: { telegram_id?: number; telegram_username?: string; role: MerchantMemberRole },
): Promise<MerchantMember> {
  const res = await adminClient.post(`/v1/crowd/admin/merchants/${merchantId}/members`, data)
  return res.data
}

export async function updateMerchantMember(
  merchantId: string,
  memberId: string,
  role: MerchantMemberRole,
): Promise<MerchantMember> {
  const res = await adminClient.put(
    `/v1/crowd/admin/merchants/${merchantId}/members/${memberId}`,
    { role },
  )
  return res.data
}

export async function removeMerchantMember(
  merchantId: string,
  memberId: string,
): Promise<void> {
  await adminClient.delete(`/v1/crowd/admin/merchants/${merchantId}/members/${memberId}`)
}

// ── Backoffice: Users ────────────────────────────────────────────────────────

export interface AdminUserListItem {
  id: string
  telegram_id: number
  telegram_username: string | null
  first_name: string | null
  last_name: string | null
  photo_url: string | null
  language_code: string | null
  is_premium: boolean
  is_active: boolean
  is_mock: boolean
  owns_shops_count: number
  member_of_count: number
  last_active_at: string | null
  created_at: string | null
  source: 'merchant' | 'public'
}

export interface AdminUserListResponse {
  items: AdminUserListItem[]
  total: number
}

export interface AdminUsersParams {
  skip?: number
  limit?: number
  search?: string
  has_merchant?: boolean
  is_active?: boolean
  is_premium?: boolean
}

export async function listAdminUsers(
  params: AdminUsersParams = {},
): Promise<AdminUserListResponse> {
  const res = await adminClient.get('/v1/crowd/admin/users', { params: { limit: 50, ...params } })
  return res.data
}

export interface AdminUserOwnedShop {
  id: string
  business_name: string
  slug: string | null
  is_verified: boolean
  created_at: string | null
}

export interface AdminUserMembership {
  id: string
  merchant_id: string
  merchant_name: string | null
  role: MerchantMemberRole
  created_at: string | null
}

export interface AdminUserRecentShopView {
  merchant_id: string
  business_name: string
  slug: string | null
  view_count: number
  last_viewed_at: string | null
}

export interface AdminUserDetail {
  id: string
  telegram_id: number
  telegram_username: string | null
  first_name: string | null
  last_name: string | null
  photo_url: string | null
  language_code: string | null
  is_premium: boolean
  is_active: boolean
  is_mock: boolean
  created_at: string | null
  last_active_at: string | null
  source?: 'merchant' | 'public'
  public_user_id: string | null
  owned_shops: AdminUserOwnedShop[]
  memberships: AdminUserMembership[]
  activity_30d: {
    product_views: number
    sessions: number
    product_likes: number
    merchant_likes_total: number
  }
  recent_shops_viewed: AdminUserRecentShopView[]
}

export async function getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  const res = await adminClient.get(`/v1/crowd/admin/users/${userId}`)
  return res.data
}

export async function deleteAdminUser(userId: string): Promise<void> {
  await adminClient.delete(`/v1/crowd/admin/users/${userId}`)
}

export async function deleteAdminShop(merchantId: string): Promise<void> {
  await adminClient.delete(`/v1/crowd/admin/merchants/${merchantId}`)
}

export interface TopUserRow {
  rank: number
  id: string
  telegram_id: number
  telegram_username: string | null
  first_name: string | null
  last_name: string | null
  language_code: string | null
  score: number
  metric: 'views' | 'sessions' | 'likes'
}

export async function getTopUsers(
  days = 30,
  limit = 50,
  metric: 'views' | 'sessions' | 'likes' = 'views',
): Promise<TopUserRow[]> {
  const res = await adminClient.get('/v1/crowd/admin/users/top', {
    params: { days, limit, metric },
  })
  return res.data
}

export interface UserGrowthPoint {
  date: string
  new_users: number
  new_public_users: number
}

export interface UserGrowthResponse {
  period_days: number
  total_users: number
  total_public_users: number
  daily: UserGrowthPoint[]
}

export async function getUserGrowth(days = 90): Promise<UserGrowthResponse> {
  const res = await adminClient.get('/v1/crowd/admin/users/growth', { params: { days } })
  return res.data
}

// ── Backoffice: Merchant bots ────────────────────────────────────────────────

export type BotStatus = 'PENDING' | 'ACTIVE' | 'REVOKED' | 'FAILED'

export interface BotStats {
  period_days: number
  total: number
  by_status: Record<string, number>
  active_total: number
  managed_count: number
  byo_count: number
  channel_linked_count: number
  candidates_total: number
  total_merchants: number
  connected_today: number
  daily: { date: string; new_bots: number }[]
}

export interface BotListItem {
  id: string
  merchant_id: string
  merchant_name: string | null
  status: BotStatus | null
  is_managed: boolean
  bot_username: string | null
  bot_telegram_id: number | null
  channel_id: number | null
  channel_title: string | null
  channel_username: string | null
  channel_linked_at: string | null
  candidate_count: number
  created_at: string | null
  updated_at: string | null
}

export interface BotsListResponse {
  items: BotListItem[]
  total: number
}

export interface BotsParams {
  skip?: number
  limit?: number
  search?: string
  status?: BotStatus
}

export async function getBotStats(days = 30): Promise<BotStats> {
  const res = await adminClient.get('/v1/crowd/admin/bots/stats', { params: { days } })
  return res.data
}

export async function listBots(params: BotsParams = {}): Promise<BotsListResponse> {
  const res = await adminClient.get('/v1/crowd/admin/bots', { params: { limit: 50, ...params } })
  return res.data
}
